/*
  Timeline tab — pure helpers (UI overhaul wave 2, screen 10 idea 6: zoom Week | Month | Quarter and
  grouping by main task, category or building). No React. Unit-tested in project-detail-w2b-model.test.js.

  Dates are calendar days ('2026-10-15' from the date fields): they are read as UTC midnight and never
  shifted by the time zone.
*/
import { formatExecutionTaskTitle } from './executionModel';

const DAY = 24 * 60 * 60 * 1000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const text = (value) => String(value ?? '').trim();

export const TIMELINE_ZOOMS = [
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'quarter', label: 'Quarter' },
];
export const TIMELINE_GROUPS = [
  { key: 'task', label: 'Main task' },
  { key: 'category', label: 'Category' },
  { key: 'building', label: 'Building' },
];
/** Minimum width of one period column (px) per zoom. */
export const PERIOD_WIDTH = { week: 72, month: 120, quarter: 168 };

/** 'YYYY-MM-DD' (or an ISO date-time) → UTC-midnight Date, else null. */
export function parseDay(value) {
  const match = text(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Today as a UTC-midnight Date in the viewer's calendar. */
export function todayDay(now = new Date()) {
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

export function startOfPeriod(date, zoom) {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  if (zoom === 'week') {
    const weekday = (date.getUTCDay() + 6) % 7; // Monday = 0
    return new Date(date.getTime() - weekday * DAY);
  }
  if (zoom === 'quarter') return new Date(Date.UTC(y, m - (m % 3), 1));
  return new Date(Date.UTC(y, m, 1));
}

export function addPeriods(date, zoom, count = 1) {
  if (zoom === 'week') return new Date(date.getTime() + count * 7 * DAY);
  const months = zoom === 'quarter' ? 3 * count : count;
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
}

export function periodLabel(start, zoom) {
  const y = start.getUTCFullYear();
  const m = start.getUTCMonth();
  if (zoom === 'week') return { label: `${start.getUTCDate()} ${MONTHS[m]}`, sub: m === 0 && start.getUTCDate() <= 7 ? String(y) : '' };
  if (zoom === 'quarter') return { label: `Q${Math.floor(m / 3) + 1} ${y}`, sub: '' };
  return { label: MONTHS[m], sub: m === 0 ? String(y) : '' };
}

export const shortDate = (date) => (date ? `${String(date.getUTCDate()).padStart(2, '0')} ${MONTHS[date.getUTCMonth()]}` : '');
const fullDate = (date) => (date ? `${shortDate(date)} ${date.getUTCFullYear()}` : '');

/** "01–08 Oct", "22 Oct – 05 Nov", "15 Oct" */
export function formatSpan(start, end) {
  if (!start && !end) return '';
  if (!start || !end || start.getTime() === end.getTime()) return shortDate(start || end);
  if (start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear()) {
    return `${String(start.getUTCDate()).padStart(2, '0')}–${shortDate(end)}`;
  }
  return `${shortDate(start)} – ${shortDate(end)}`;
}

/**
 * Execution tasks with a start and/or due date → timeline items:
 * { id, title, root (main task title), depth, start, end, point (one date only), status, category, building }
 */
export function timelineItemsFromTasks(tasks = [], tree = null) {
  const byId = tree?.byId || new Map(tasks.map((task) => [task.id, task]));
  const rootOf = (task) => {
    let current = task;
    const seen = new Set();
    while (current?.parent_id && byId.has(current.parent_id) && !seen.has(current.id)) {
      seen.add(current.id);
      current = byId.get(current.parent_id);
    }
    return current;
  };
  const items = [];
  tasks.forEach((task) => {
    const start = parseDay(task.start_date);
    const due = parseDay(task.due_date);
    if (!start && !due) return;
    const [from, to] = start && due && due < start ? [due, start] : [start || due, due || start];
    const root = rootOf(task);
    const depth = Math.max(0, Number(task.level || 1) - 1);
    items.push({
      id: task.id,
      title: depth ? formatExecutionTaskTitle(task, byId) : text(task.title) || 'Task',
      root: text(root?.title) || 'Task',
      rootOrder: tasks.indexOf(root),
      depth,
      start: from,
      end: to,
      point: !(start && due),
      status: text(task.status) || 'Not Started',
      category: text(task.category) || text(root?.category),
      building: text(task.zone),
    });
  });
  return items;
}

/**
 * Groups of items: by main task (in task order), category or building (A–Z, "Whole project" last).
 * By main task, the main task's own dates sit on the group line (`own`) and the rows are its sub-items.
 */
export function groupTimelineItems(items = [], group = 'task') {
  const groups = new Map();
  const keyOf = (item) => {
    if (group === 'category') return item.category || 'No category';
    if (group === 'building') return item.building || 'Whole project';
    return item.root;
  };
  items.forEach((item) => {
    const key = keyOf(item);
    if (!groups.has(key)) groups.set(key, { key, label: key, items: [], order: item.rootOrder });
    groups.get(key).items.push(item);
  });
  const list = [...groups.values()];
  list.forEach((entry) => {
    entry.start = new Date(Math.min(...entry.items.map((item) => item.start.getTime())));
    entry.end = new Date(Math.max(...entry.items.map((item) => item.end.getTime())));
    if (group === 'task') {
      entry.own = entry.items.find((item) => item.depth === 0) || null;
      entry.items = entry.items.filter((item) => item !== entry.own);
    }
    entry.count = entry.items.length + (entry.own ? 1 : 0);
    entry.items.sort((a, b) => a.start - b.start || a.depth - b.depth || a.title.localeCompare(b.title, undefined, { numeric: true }));
  });
  const fallback = group === 'building' ? 'Whole project' : 'No category';
  if (group === 'task') list.sort((a, b) => a.order - b.order);
  else list.sort((a, b) => (a.key === fallback) - (b.key === fallback) || a.key.localeCompare(b.key, undefined, { numeric: true }));
  return list;
}

/**
 * The time axis for some items: periods from the first to the last date (today included when it is near),
 * and a function placing a date span as percentages of the axis.
 * → { start, end, periods: [{ key, start, label, sub, current }], place(start, end) → { left, width } }
 */
export function buildTimeAxis(items = [], zoom = 'month', { today = todayDay() } = {}) {
  const valid = items.filter((item) => item.start && item.end);
  let first = valid.length ? new Date(Math.min(...valid.map((item) => item.start.getTime()))) : today;
  let last = valid.length ? new Date(Math.max(...valid.map((item) => item.end.getTime()))) : today;
  // Keep today on the axis when it is within a year of the plan (where people look first).
  if (Math.abs(today - first) < 366 * DAY || Math.abs(today - last) < 366 * DAY) {
    if (today < first) first = today;
    if (today > last) last = today;
  }
  const start = startOfPeriod(first, zoom);
  const periods = [];
  let cursor = start;
  const currentStart = startOfPeriod(today, zoom).getTime();
  while (cursor <= last || periods.length === 0) {
    const { label, sub } = periodLabel(cursor, zoom);
    periods.push({ key: cursor.toISOString().slice(0, 10), start: cursor, label, sub, current: cursor.getTime() === currentStart });
    cursor = addPeriods(cursor, zoom, 1);
    if (periods.length > 520) break; // ten years of weeks: enough for any project
  }
  const end = cursor;
  const span = end - start;
  const place = (from, to) => {
    const left = ((from - start) / span) * 100;
    const right = ((to.getTime() + DAY - start) / span) * 100;
    return { left: Math.max(0, left), width: Math.max(0.6, right - left) };
  };
  return { start, end, periods, place, fullDate };
}
